'use client';

import { useState } from 'react';
import { cn } from '@/app/lib/utils';

interface BlogCardImageProps {
  src: string;
  alt: string;
  category?: string;
  className?: string;
}

const Placeholder = ({ category }: { category?: string }) => (
  <div className="absolute inset-0 bg-gradient-to-br from-[#506C83]/20 to-[#181A1D]/10 flex items-center justify-center">
    <span className="text-[#506C83] text-5xl font-bold opacity-20">
      {category?.[0]?.toUpperCase() || 'B'}
    </span>
  </div>
);

/**
 * Blog card cover image with error fallback.
 * Uses native <img> so onError fires reliably when images are not yet deployed (404).
 * Avoids broken-image icon; shows gradient + letter placeholder instead.
 */
export function BlogCardImage({ src, alt, category, className }: BlogCardImageProps) {
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  if (error) {
    return <Placeholder category={category} />;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={cn(
        'absolute inset-0 h-full w-full object-cover transition-opacity duration-300',
        loaded ? 'opacity-100' : 'opacity-0',
        className
      )}
      onLoad={() => setLoaded(true)}
      onError={() => setError(true)}
      loading="lazy"
      decoding="async"
    />
  );
}
